// src/components/forms/ContactForm.js

import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { ThemeContext } from '../../context/ThemeContext';
import { contactService } from '../../api';

const FormContainer = styled.div`
  width: 100%;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.theme.text};
`;

const FormInput = styled.input`
  padding: 0.8rem 1rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background: ${props => props.theme.inputBg || props.theme.backgroundSecondary};
  color: ${props => props.theme.text};
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => `${props.theme.primary}33`};
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary || props.theme.text + '80'};
  }
`;

const FormTextarea = styled.textarea`
  padding: 0.8rem 1rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background: ${props => props.theme.inputBg || props.theme.backgroundSecondary};
  color: ${props => props.theme.text};
  font-size: 1rem;
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => `${props.theme.primary}33`};
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary || props.theme.text + '80'};
  }
`;

const SubmitButton = styled.button`
  padding: 0.8rem 1.5rem;
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
  
  &:hover {
    background: ${props => props.theme.primaryDark || props.theme.primary};
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    background: ${props => props.theme.textSecondary || props.theme.text + '40'};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SuccessMessage = styled(motion.div)`
  padding: 1rem;
  background: #d4edda;
  color: #155724;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ErrorMessage = styled(motion.div)`
  padding: 1rem;
  background: #f8d7da;
  color: #721c24;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ContactForm = ({ recipient }) => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      // Préparation des données à envoyer
      const contactData = {
        ...formData,
        recipient_id: recipient.id,
        recipient_email: recipient.email,
        recipient_name: `${recipient.first_name} ${recipient.last_name}`
      };
      
      // Envoi du message via l'API
      await contactService.sendMessage(contactData);
      
      // Réinitialisation du formulaire
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
      // Affichage du message de succès
      setSuccess(true);
      
      // Masquage du message de succès après 5 secondes
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
      
    } catch (err) {
      console.error("Error sending message:", err);
      setError(err.response?.data?.detail || t('errorSendingMessage'));
    } finally {
      setLoading(false);
    }
  };
  
  const isFormValid = formData.name && formData.email && formData.subject && formData.message;
  
  return (
    <FormContainer>
      {success && (
        <SuccessMessage
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <i className="fas fa-check-circle"></i>
          {t('messageSentSuccess')}
        </SuccessMessage>
      )}
      
      {error && (
        <ErrorMessage
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </ErrorMessage>
      )}
      
      <StyledForm onSubmit={handleSubmit}>
        <FormGroup>
          <FormLabel htmlFor="name" theme={theme}>{t('yourName')}</FormLabel>
          <FormInput
            id="name"
            name="name"
            type="text"
            placeholder={t('enterYourName')}
            value={formData.name}
            onChange={handleChange}
            required
            theme={theme}
          />
        </FormGroup>
        
        <FormGroup>
          <FormLabel htmlFor="email" theme={theme}>{t('yourEmail')}</FormLabel>
          <FormInput
            id="email"
            name="email"
            type="email"
            placeholder={t('enterYourEmail')}
            value={formData.email}
            onChange={handleChange}
            required
            theme={theme}
          />
        </FormGroup>
        
        <FormGroup>
          <FormLabel htmlFor="subject" theme={theme}>{t('subject')}</FormLabel>
          <FormInput
            id="subject"
            name="subject"
            type="text"
            placeholder={t('enterSubject')}
            value={formData.subject}
            onChange={handleChange}
            required
            theme={theme}
          />
        </FormGroup>
        
        <FormGroup>
          <FormLabel htmlFor="message" theme={theme}>{t('message')}</FormLabel>
          <FormTextarea
            id="message"
            name="message"
            placeholder={t('enterYourMessage')}
            value={formData.message}
            onChange={handleChange}
            required
            theme={theme}
          />
        </FormGroup>
        
        <SubmitButton
          type="submit"
          disabled={loading || !isFormValid}
          theme={theme}
        >
          {loading ? (
            <span>
              <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
              {t('sending')}
            </span>
          ) : (
            t('sendMessage')
          )}
        </SubmitButton>
      </StyledForm>
    </FormContainer>
  );
};

export default ContactForm;